import { IdentityLink } from "@/components/identity-link";
import { InsightsConfig } from "@/components/insights-dock";
import { INSIGHTS_SUGGESTIONS } from "@/lib/insights-suggestions";
import { TabbedPage } from "@/components/page-templates";
import { RequireScope } from "@/components/require-scope";
import { TableRowContextMenu } from "@/components/table-row-context-menu";
import type { Action } from "@/components/ui/MoreActions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { SearchBar } from "@/components/ui/SearchBar";
import { Switch } from "@/components/ui/Switch";
import { Dialog } from "@/components/ui/Dialog";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/Sheet";
import { Text } from "@/components/ui/Text";
import { ExclusionsTab, type ExclusionSheetState } from "./ExclusionsTab";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { Stack } from "@/components/ui/Stack";
import { type Column, Table } from "@/components/ui/Table";
import { type BadgeProps } from "@/components/ui/Badge";
import {
  Plus,
  Shield,
  Ellipsis,
  Loader2,
  ChevronRight,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useQueryClient } from "@tanstack/react-query";
import { useMembers } from "@gram/client/react-query/members.js";
import { useRiskCreatePolicyMutation } from "@gram/client/react-query/riskCreatePolicy.js";
import {
  invalidateAllRiskListPolicies,
  useRiskListPolicies,
} from "@gram/client/react-query/riskListPolicies.js";
import {
  invalidateAllRiskListSessionQuarantines,
  useRiskListSessionQuarantines,
} from "@gram/client/react-query/riskListSessionQuarantines.js";
import { useRiskReleaseSessionQuarantineMutation } from "@gram/client/react-query/riskReleaseSessionQuarantine.js";
import { useRiskPoliciesDeleteMutation } from "@gram/client/react-query/riskPoliciesDelete.js";
import { useRoles } from "@gram/client/react-query/roles.js";
import type { RiskPolicy } from "@gram/client/models/components/riskpolicy.js";
import type { SessionQuarantine } from "@gram/client/models/components/sessionquarantine.js";
import type { AccessMember } from "@gram/client/models/components/accessmember.js";
import {
  RULE_CATEGORY_META,
  DETECTION_RULES,
  RULE_FAMILY_OF,
  RULE_FAMILY_ORDER,
  type DetectionRule,
  type RuleCategory,
  type PolicyAction,
} from "./policy-data";
import { POLICY_TYPE_LABEL, policyTypeOf } from "./policy-type";
import { cn } from "@/lib/utils";
import { dateTimeFormatters, HumanizeDateTime } from "@/lib/dates";
import { useDetectionRulesStore } from "./detection-rules-data";
import { useTelemetry } from "@/contexts/Telemetry";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { useRoutes } from "@/routes";
import { Outlet } from "react-router";
import {
  ACTION_OPTIONS,
  categoriesToPayload,
  mcpCompatibleAction,
} from "./policy-form";
import { useDetectorMode } from "./use-detector-mode";
import {
  getPolicyDeleteImpactText,
  getPolicyDeleteRuleListItems,
  getPolicyRuleGroupNamesForDeleteDialog,
} from "./policy-delete-dialog";
import { DetectionRulesTab } from "./DetectionRules";
import {
  POLICY_CENTER_TABS,
  policyCenterTabLabel,
  policyCenterTabs,
  resolvePolicyCenterTab,
  type PolicyCenterTab,
} from "./policy-center-tabs";
import { MCPApprovalsTab } from "./MCPApprovalsTab";
import {
  compareMembersByName,
  compareRolesByName,
  memberDisplayName,
} from "./audience-principals";
import { ToolApprovalsTab } from "./tool-approvals/ToolApprovalsTab";
import { ShadowAITargetsTab } from "./ShadowAITargetsTab";
import { detectionRulesTabRuleIds } from "./detection-rules-data";
import { SeverityBadge } from "./risk-ui";
import { policySummary } from "./policy-summary";
import { policyEnabledActionLabel } from "./policy-enabled";
import {
  togglePolicyEnabledVariables,
  useTogglePolicyEnabled,
} from "./use-toggle-policy-enabled";

/** Per-policy config for the Non-Corporate Accounts category: the list of
 *  email domains treated as corporate. Rendered inside the category's
 *  Customize sheet; the parsed list rides on the create/update payload as
 *  approved_email_domains while the category is selected. */
function ApprovedDomainsConfig({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Approved email domains</Label>
      <p className="text-muted-foreground text-xs">
        Sessions from AI accounts whose email domain is not in this list are
        flagged by the unapproved-domain rule. Matching is exact per domain, so
        list subdomains explicitly; a leading '@' is allowed. Until at least one
        domain is configured, only the personal-account rule fires.
      </p>
      <Input
        value={value}
        onChange={onChange}
        placeholder="e.g. acme.com, corp.acme.com"
      />
    </div>
  );
}

/** Side-sheet to pick which rules within a built-in detector category are
 *  active. Disabling a rule adds its canonical rule_id to the policy's
 *  disabled_rules; a search box tames the large categories (e.g. 222 secrets). */
export function CustomizeRulesSheet({
  category,
  selectedCategories,
  setSelectedCategories,
  disabledRules,
  setDisabledRules,
  approvedDomains,
  setApprovedDomains,
  onClose,
}: {
  category: RuleCategory;
  selectedCategories: Set<RuleCategory>;
  setSelectedCategories: (v: Set<RuleCategory>) => void;
  disabledRules: Set<string>;
  setDisabledRules: (v: Set<string>) => void;
  approvedDomains: string;
  setApprovedDomains: (v: string) => void;
  onClose: () => void;
}): JSX.Element {
  const meta = RULE_CATEGORY_META[category];
  const rules = DETECTION_RULES[category].filter((r) => !r.hidden);
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filtered = query
    ? rules.filter((r) => r.title.toLowerCase().includes(query))
    : rules;
  const enabledCount = rules.filter((r) => !disabledRules.has(r.id)).length;

  const setRule = (id: string, on: boolean) => {
    const next = new Set(disabledRules);
    if (on) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setDisabledRules(next);
    if (on && !selectedCategories.has(category)) {
      const cats = new Set(selectedCategories);
      cats.add(category);
      setSelectedCategories(cats);
    }
  };
  const bulk = (on: boolean) => {
    const next = new Set(disabledRules);
    for (const r of rules) {
      if (on) {
        next.delete(r.id);
      } else {
        next.add(r.id);
      }
    }
    setDisabledRules(next);
  };

  // Large categories (currently just secrets, ~200 rules) classify into named
  // families so the list is navigable; everything else renders flat.
  const grouper = RULE_FAMILY_OF[category];
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (family: string) => {
    const next = new Set(expandedGroups);
    if (next.has(family)) {
      next.delete(family);
    } else {
      next.add(family);
    }
    setExpandedGroups(next);
  };
  const bulkGroup = (familyRules: DetectionRule[], on: boolean) => {
    const next = new Set(disabledRules);
    for (const r of familyRules) {
      if (on) {
        next.delete(r.id);
      } else {
        next.add(r.id);
      }
    }
    setDisabledRules(next);
    if (on && !selectedCategories.has(category)) {
      const cats = new Set(selectedCategories);
      cats.add(category);
      setSelectedCategories(cats);
    }
  };
  // Ordered, non-empty families over the (search-)filtered rules.
  const groupedRules = grouper
    ? RULE_FAMILY_ORDER.map((family) => ({
        family,
        rules: filtered.filter((r) => grouper(r) === family),
      })).filter((g) => g.rules.length > 0)
    : [];

  return (
    <Sheet
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-md">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle>Customize {meta.label}</SheetTitle>
          <SheetDescription>
            Pick which rules in this category are active. All are on by default.
          </SheetDescription>
        </SheetHeader>
        {category === "account_identity" && (
          <div className="border-border mx-6 mt-3 border-b pb-4">
            <ApprovedDomainsConfig
              value={approvedDomains}
              onChange={setApprovedDomains}
            />
          </div>
        )}
        <div className="px-6 pt-3">
          <Input
            value={search}
            onChange={setSearch}
            placeholder={`Search ${rules.length} ${meta.label.toLowerCase()} rules…`}
          />
        </div>
        <div className="text-muted-foreground flex items-center justify-between px-6 py-2 text-xs">
          <span>
            {enabledCount} of {rules.length} active
          </span>
          <span className="flex gap-3">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => bulk(true)}
            >
              Enable all
            </button>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => bulk(false)}
            >
              Disable all
            </button>
          </span>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-6">
          {grouper
            ? groupedRules.map(({ family, rules: familyRules }) => {
                const open = expandedGroups.has(family) || query.length > 0;
                const enabled = familyRules.filter(
                  (r) => !disabledRules.has(r.id),
                ).length;
                return (
                  <div
                    key={family}
                    className="border-border border-b last:border-b-0"
                  >
                    <div className="flex items-center gap-2 px-2 py-2">
                      <button
                        type="button"
                        onClick={() => toggleGroup(family)}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <ChevronRight
                          className={cn(
                            "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
                            open && "rotate-90",
                          )}
                        />
                        <span className="truncate text-sm font-medium">
                          {family}
                        </span>
                        <span className="text-muted-foreground shrink-0 text-xs">
                          {enabled}/{familyRules.length}
                        </span>
                      </button>
                      <Switch
                        checked={enabled === familyRules.length}
                        onCheckedChange={(on) => bulkGroup(familyRules, on)}
                      />
                    </div>
                    {open && (
                      <div className="pb-1 pl-4">
                        {familyRules.map((rule) => (
                          <RuleToggleRow
                            key={rule.id}
                            rule={rule}
                            checked={!disabledRules.has(rule.id)}
                            onToggle={(on) => setRule(rule.id, on)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            : filtered.map((rule) => (
                <RuleToggleRow
                  key={rule.id}
                  rule={rule}
                  checked={!disabledRules.has(rule.id)}
                  onToggle={(on) => setRule(rule.id, on)}
                />
              ))}
          {grouper && groupedRules.length === 0 && (
            <p className="text-muted-foreground px-2 py-6 text-center text-xs">
              No rules match.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function RuleToggleRow({
  rule,
  checked,
  onToggle,
}: {
  rule: DetectionRule;
  checked: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <div className="hover:bg-muted flex items-center justify-between gap-3 px-2 py-2 text-sm">
      <span className="min-w-0 truncate">{rule.title}</span>
      <Switch checked={checked} onCheckedChange={onToggle} />
    </div>
  );
}

type PolicyKind = "risk" | "prompt";
type PolicyAudienceType = "everyone" | "targeted";
type PolicyAudienceChoice = "everyone" | "users" | "roles";

type PolicyRow = { kind: PolicyKind; policy: RiskPolicy };

const USER_SEARCH_RESULT_LIMIT = 10;

function policyAudienceSummary(row: PolicyRow): string {
  const audience = policyAudienceTargets(row);
  if (row.policy.action !== "require_approval") {
    return audience;
  }
  const approvers = row.policy.approvalConfig?.approvers.length ?? 0;
  return `${audience} · ${approvers} ${approvers === 1 ? "approver" : "approvers"}`;
}

function policyAudienceTargets(row: PolicyRow): string {
  if (row.kind === "prompt") {
    return "Everyone";
  }
  if (row.policy.audienceType !== "targeted") {
    return "Everyone";
  }

  const count = row.policy.audiencePrincipalUrns.length;
  if (count === 1) {
    return "1 target";
  }
  return `${count} targets`;
}

function policyAudienceChoiceForSelection(
  audienceType: PolicyAudienceType,
  principalUrns: Set<string>,
): PolicyAudienceChoice {
  if (audienceType === "everyone") {
    return "everyone";
  }

  const hasUser = [...principalUrns].some((urn) => urn.startsWith("user:"));
  if (hasUser) {
    return "users";
  }

  const hasRole = [...principalUrns].some((urn) => urn.startsWith("role:"));
  return hasRole ? "roles" : "users";
}

function filterAudiencePrincipalsForChoice(
  principalUrns: Set<string>,
  choice: PolicyAudienceChoice,
): Set<string> {
  if (choice === "everyone") {
    return new Set<string>();
  }

  const prefix = choice === "users" ? "user:" : "role:";
  return new Set([...principalUrns].filter((urn) => urn.startsWith(prefix)));
}

function memberInitials(member: Pick<AccessMember, "email" | "name">): string {
  const source = member.name.trim() || member.email.trim();
  const initials = source
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return initials || "?";
}

function memberMatchesSearch(member: AccessMember, search: string): boolean {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) {
    return false;
  }

  return (
    member.name.toLowerCase().includes(normalizedSearch) ||
    member.email.toLowerCase().includes(normalizedSearch)
  );
}

function isPromptPolicy(policy: RiskPolicy): boolean {
  return policy.policyType === "prompt_based";
}

/** Policy name over what the policy detects. The second line is dropped when
 *  the name already says it, which is the common case for auto-generated names
 *  ("Secrets Exposure Flagger" over "Secrets"). */
function PolicyNameCell({ row }: { row: PolicyRow }): JSX.Element {
  const mode = useDetectorMode();
  const summary = policySummary(row.policy, mode);

  return (
    <span className="flex min-w-0 flex-col gap-0.5 py-0.5">
      <span className="flex min-w-0 items-center gap-1.5 font-medium">
        <span
          className={cn(
            "truncate",
            !row.policy.enabled && "text-muted-foreground",
          )}
        >
          {row.policy.name}
        </span>
        {row.kind === "prompt" && (
          <SimpleTooltip tooltip="Prompt-based policy">
            <Sparkles
              aria-label="Prompt-based policy"
              className="text-muted-foreground h-3.5 w-3.5 shrink-0"
            />
          </SimpleTooltip>
        )}
      </span>
      {summary && (
        <SimpleTooltip tooltip={summary.text}>
          <span
            className={cn(
              "text-muted-foreground truncate text-xs",
              summary.kind === "prompt" && "italic",
            )}
          >
            {summary.text}
          </span>
        </SimpleTooltip>
      )}
    </span>
  );
}

/** How the policy decides, for the Type column: Built-in, Prompt-based or
 *  Shadow AI. */
function PolicyTypeBadge({ policy }: { policy: RiskPolicy }): JSX.Element {
  return (
    <Badge variant="neutral">
      <Badge.Text>{POLICY_TYPE_LABEL[policyTypeOf(policy)]}</Badge.Text>
    </Badge>
  );
}

const POLICY_TYPE_COLUMN: Column<PolicyRow> = {
  key: "type",
  header: "Type",
  // Wide enough for the longest badge ("SHADOW AI") at the table's
  // narrowest supported width; the Action column already clips at 0.7fr.
  width: "1fr",
  render: (row) => (
    <span className="inline-flex">
      <PolicyTypeBadge policy={row.policy} />
    </span>
  ),
};

/** Compact relative date for the policy table's Created/Updated columns, with
 *  the exact timestamp on hover. */
function PolicyDateCell({ date }: { date: Date }): JSX.Element {
  return (
    <SimpleTooltip tooltip={dateTimeFormatters.full.format(date)}>
      <span className="text-muted-foreground text-sm whitespace-nowrap">
        <HumanizeDateTime date={date} includeTime={false} />
      </span>
    </SimpleTooltip>
  );
}

function truncateSessionID(sessionID: string): string {
  if (sessionID.length <= 18) return sessionID;
  return `${sessionID.slice(0, 8)}...${sessionID.slice(-6)}`;
}

/** Assistant context for the Detection Rules tab: assembled from the static
 *  client-side rule catalog so the other tabs pay no extra queries for it.
 *  Rule-activity questions route through the finding-level tools. */
function detectionRulesInsightsContext(): string {
  return [
    "Page: Guardrails, Detection Rules tab — the catalog of built-in and custom detection rules that policies compose.",
    `Built-in rule ids: ${detectionRulesTabRuleIds().join(", ")}.`,
    "Custom rules are organization-defined CEL expressions with ids prefixed 'custom.'; list them with listCustomDetectionRules.",
    "For rule activity, query findings by rule_id via listRiskResultsForAgent (match content is redacted).",
    "Never echo match_redacted values verbatim. Refer to findings by rule_id and source.",
  ].join(" ");
}

/** The page-level primary action follows the active tab: each tab creates its
 *  own kind of resource. */
function policyCenterHeaderAction(
  activeTab: PolicyCenterTab,
  actions: {
    newPolicy: () => void;
    newDetectionRule: () => void;
    newExclusion: () => void;
  },
): { label: string; onClick: () => void } | null {
  switch (activeTab) {
    case "policies":
      return { label: "New Policy", onClick: actions.newPolicy };
    case "detection-rules":
      return {
        label: "Custom Detection Rule",
        onClick: actions.newDetectionRule,
      };
    // The targets library carries its own add button in its toolbar.
    case "shadow-ai-targets":
      return null;
    // Access is decided per server, on the row or the server page; the queue
    // itself is fed by requests and observed traffic.
    case "mcp-approvals":
      return null;
    // Requests come from held calls; approval policies are created from the
    // Policies tab.
    case "tool-approvals":
      return null;
    case "exclusions":
      return { label: "Set up Exclusion Rule", onClick: actions.newExclusion };
    // Quarantines are event-driven; the tab has no creation affordance.
    case "quarantines":
      return null;
  }
}

export default function PolicyCenter(): JSX.Element {
  return (
    <RequireScope scope="org:admin" level="page">
      <PolicyCenterContent />
    </RequireScope>
  );
}

// Layout route wrapper: the policy list and the policy detail subpage render
// through this Outlet (see routes.tsx policyCenter).
export function PolicyCenterRoot(): JSX.Element {
  return <Outlet />;
}

function PolicyCenterContent() {
  const mode = useDetectorMode();
  const queryClient = useQueryClient();
  const routes = useRoutes();
  const telemetry = useTelemetry();
  const { data, isLoading } = useRiskListPolicies();
  const {
    data: quarantinesData,
    isLoading: quarantinesLoading,
    isError: quarantinesError,
    refetch: refetchQuarantines,
  } = useRiskListSessionQuarantines();
  const nlEnabled = telemetry.isFeatureEnabled("gram-prompt-policies") ?? false;

  const policyRows = useMemo(
    (): PolicyRow[] =>
      (data?.policies ?? [])
        .filter((policy) => nlEnabled || !isPromptPolicy(policy))
        .map((policy) => {
          const kind: PolicyKind = isPromptPolicy(policy) ? "prompt" : "risk";
          return { kind, policy };
        })
        .sort(
          (a, b) => b.policy.createdAt.getTime() - a.policy.createdAt.getTime(),
        ),
    [data?.policies, nlEnabled],
  );

  const [policyToDelete, setPolicyToDelete] = useState<PolicyRow | null>(null);

  const [requestedTab] = useQueryState(
    "tab",
    parseAsStringLiteral(POLICY_CENTER_TABS).withDefault("policies"),
  );
  const toolApprovalsFlag = useFeatureFlag(FEATURE_FLAGS.toolApprovals).status;
  const toolApprovalsEnabled = toolApprovalsFlag === "enabled";
  // A hidden tab's link lands on Policies once the flags have loaded.
  const activeTab =
    toolApprovalsFlag === "loading"
      ? requestedTab
      : resolvePolicyCenterTab(requestedTab, {
          toolApprovals: toolApprovalsEnabled,
        });
  const [exclusionSheet, setExclusionSheet] =
    useState<ExclusionSheetState | null>(null);
  // The Detection Rules tab's create sheet is owned here so the page-level
  // primary action can open it. Leaving the tab closes it — otherwise the
  // sheet would silently reopen when the tab is next visited.
  const [ruleCreateOpen, setRuleCreateOpen] = useState(false);
  useEffect(() => {
    if (activeTab !== "detection-rules") setRuleCreateOpen(false);
  }, [activeTab]);

  // Deep-link support: `?policy=<id>` redirects to that policy's detail page.
  // The command palette uses this since policies have no per-item list route.
  const [policyParam] = useQueryState("policy");
  const openedPolicyRef = useRef<string | null>(null);

  const invalidate = useCallback(() => {
    void invalidateAllRiskListPolicies(queryClient);
  }, [queryClient]);

  const createMutation = useRiskCreatePolicyMutation({
    onSuccess: invalidate,
  });

  const deleteMutation = useRiskPoliciesDeleteMutation({
    onSuccess: () => {
      setPolicyToDelete(null);
      invalidate();
    },
  });
  const releaseQuarantineMutation = useRiskReleaseSessionQuarantineMutation({
    onSuccess: () => {
      void invalidateAllRiskListSessionQuarantines(queryClient);
    },
  });

  const toggleEnabledMutation = useTogglePolicyEnabled();

  const handleToggleEnabled = (row: PolicyRow) => {
    toggleEnabledMutation.mutate(
      togglePolicyEnabledVariables(
        row.policy.id,
        row.policy.name,
        !row.policy.enabled,
      ),
    );
  };

  // Redirect a deep-linked policy to its detail page once its data has loaded.
  // Guarded by a ref so it fires once per id (not on every policies re-fetch),
  // and marks unknown ids handled so a stale id doesn't retry every render.
  useEffect(() => {
    if (!policyParam || isLoading) return;
    if (openedPolicyRef.current === policyParam) return;
    const policy = data?.policies?.find((p) => p.id === policyParam);
    openedPolicyRef.current = policyParam;
    if (policy) {
      routes.policyCenter.detail.goTo(policyParam);
    }
  }, [policyParam, isLoading, data, routes]);

  const handleDelete = (row: PolicyRow) => {
    setPolicyToDelete(row);
  };

  const policyActions = (row: PolicyRow): Action[] => [
    {
      label: "Edit",
      onClick: () => {
        // Both prompt and standard policies now edit on their
        // dedicated detail page.
        routes.policyCenter.detail.goTo(row.policy.id);
      },
    },
    {
      label: policyEnabledActionLabel(row.policy.enabled),
      disabled: toggleEnabledMutation.isPending,
      onClick: () => {
        setTimeout(() => handleToggleEnabled(row), 0);
      },
    },
    {
      label: "Delete",
      destructive: true,
      separatorBefore: true,
      onClick: () => {
        setTimeout(() => handleDelete(row), 0);
      },
    },
  ];

  const confirmDelete = () => {
    if (!policyToDelete) return;
    deleteMutation.mutate({ request: { id: policyToDelete.policy.id } });
  };

  const confirmDisableInstead = () => {
    if (!policyToDelete) return;
    const row = policyToDelete;
    setPolicyToDelete(null);
    toggleEnabledMutation.mutate(
      togglePolicyEnabledVariables(row.policy.id, row.policy.name, false),
    );
  };

  // Empty state for the Policies tab only. It must NOT short-circuit the whole
  // page, otherwise the Exclusions tab (and global exclusions) would be
  // unreachable for projects that have no policies yet.
  const policiesEmptyState = (
    <div className="bg-muted/20 flex flex-col items-center justify-center border border-dashed px-8 py-16">
      <div className="bg-muted/50 mb-4 flex h-12 w-12 items-center justify-center">
        <Shield className="text-muted-foreground h-6 w-6" />
      </div>
      <Text variant="subheading" className="mb-1">
        No Risk Policies
      </Text>
      <Text small muted className="mb-4 max-w-md text-center">
        Risk policies scan your chat messages for secrets and sensitive data.
        Create your first policy to get started.
      </Text>
      <Button
        onClick={() => {
          const {
            sources,
            presidioEntities,
            promptInjectionRules,
            disabledRules: payloadDisabled,
          } = categoriesToPayload(
            new Set<RuleCategory>(["secrets", "pii"]),
            new Set(),
            new Set(),
            mode,
          );
          createMutation.mutate({
            request: {
              createRiskPolicyRequestBody: {
                autoName: true,
                enabled: true,
                sources,
                presidioEntities,
                promptInjectionRules,
                disabledRules: payloadDisabled,
                customRuleIds: [],
              },
            },
          });
        }}
        disabled={createMutation.isPending}
      >
        <Button.Text className="flex gap-2">
          {createMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Get Started"
          )}
        </Button.Text>
      </Button>
    </div>
  );

  const insightsContext = [
    `Page: Guardrails, ${policyCenterTabLabel(activeTab)} tab.`,
    `Total policies: ${policyRows.length}.`,
    `Active policies: ${policyRows.filter((r) => r.policy.enabled).length}.`,
    `Policy actions: ${policyRows.map((r) => `${r.policy.name} (${r.policy.action}${r.policy.enabled ? "" : ", inactive"})`).join(", ") || "none"}.`,
    "Available risk tools: listRiskPolicies, getRiskPolicy, listRiskResultsForAgent (finding-level with match redaction), listRiskResultsByChat, listShadowMCPApprovals.",
    "Never echo match_redacted values verbatim. Refer to findings by rule_id and source.",
  ].join(" ");

  const policyColumns: Column<PolicyRow>[] = [
    {
      key: "name",
      header: "Policy",
      width: "3fr",
      render: (row) => <PolicyNameCell row={row} />,
    },
    POLICY_TYPE_COLUMN,
    {
      key: "action",
      header: "Action",
      width: "0.7fr",
      render: (row) => (
        <span className="inline-flex">
          <ActionBadge action={(row.policy.action as PolicyAction) ?? "flag"} />
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "0.5fr",
      render: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={row.policy.enabled}
            disabled={toggleEnabledMutation.isPending}
            onCheckedChange={(checked) =>
              toggleEnabledMutation.mutate(
                togglePolicyEnabledVariables(
                  row.policy.id,
                  row.policy.name,
                  checked,
                ),
              )
            }
            aria-label={row.policy.enabled ? "Disable policy" : "Enable policy"}
          />
        </div>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      width: "0.5fr",
      render: (row) => (
        <span className="inline-flex">
          <SeverityBadge score={row.policy.score} />
        </span>
      ),
    },
    {
      key: "audience",
      header: "Audience",
      width: "1fr",
      render: (row) => (
        <span className="text-muted-foreground text-sm">
          {policyAudienceSummary(row)}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      width: "0.8fr",
      render: (row) => <PolicyDateCell date={row.policy.createdAt} />,
    },
    {
      key: "updatedAt",
      header: "Updated",
      width: "0.8fr",
      render: (row) => <PolicyDateCell date={row.policy.updatedAt} />,
    },
    {
      key: "actions",
      header: "",
      width: "0.3fr",
      render: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="tertiary"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <Button.Icon>
                  <Ellipsis className="h-4 w-4" />
                </Button.Icon>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {policyActions(row).map((action) => (
                <Fragment key={action.label}>
                  {action.separatorBefore ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem
                    disabled={action.disabled}
                    className={cn(
                      "cursor-pointer",
                      action.destructive &&
                        "text-destructive focus:text-destructive",
                    )}
                    onSelect={() => action.onClick()}
                  >
                    {action.label}
                  </DropdownMenuItem>
                </Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const quarantineColumns: Column<SessionQuarantine>[] = [
    {
      key: "session",
      header: "Session",
      width: "1.4fr",
      render: (row) => (
        <span className="font-mono text-sm">
          {truncateSessionID(row.sessionId)}
        </span>
      ),
    },
    {
      key: "policy",
      header: "Policy",
      width: "1.4fr",
      render: (row) => <span className="text-sm">{row.riskPolicyName}</span>,
    },
    {
      key: "user",
      header: "User",
      width: "1fr",
      render: (row) => (
        <span className="text-muted-foreground text-sm">
          <IdentityLink identifier={row.userId ? { userId: row.userId } : null}>
            {row.userId || "Unknown user"}
          </IdentityLink>
        </span>
      ),
    },
    {
      key: "created",
      header: "Quarantined",
      width: "0.8fr",
      render: (row) => <PolicyDateCell date={row.createdAt} />,
    },
    {
      key: "actions",
      header: "",
      width: "0.4fr",
      render: (row) => (
        <Button
          variant="tertiary"
          size="sm"
          disabled={releaseQuarantineMutation.isPending}
          onClick={() => releaseQuarantine(row.id)}
        >
          <Button.Text>Release</Button.Text>
        </Button>
      ),
    },
  ];

  function releaseQuarantine(id: string) {
    releaseQuarantineMutation.mutate({
      request: { sessionQuarantineReleaseRequestBody: { id } },
    });
  }

  const headerAction = policyCenterHeaderAction(activeTab, {
    newPolicy: () => routes.policyCenter.new.goTo(),
    newDetectionRule: () => setRuleCreateOpen(true),
    newExclusion: () => setExclusionSheet({ mode: "create" }),
  });
  const policyDeleteRuleListItems = policyToDelete
    ? getPolicyDeleteRuleListItems(
        getPolicyRuleGroupNamesForDeleteDialog(policyToDelete.policy, mode),
      )
    : [];
  const policyDeleteImpactText = policyToDelete
    ? getPolicyDeleteImpactText(
        policyToDelete.policy,
        policyDeleteRuleListItems.length > 0,
      )
    : "";

  let policiesBody = (
    <Table
      columns={policyColumns}
      data={policyRows}
      rowKey={(row) => row.policy.id}
      onRowClick={(row) =>
        // Both prompt and standard policies open their dedicated detail page
        // (eval workbench for prompt, on-page editor for standard).
        routes.policyCenter.detail.goTo(row.policy.id)
      }
      renderRow={(row, rowElement) => (
        <TableRowContextMenu key={row.policy.id} actions={policyActions(row)}>
          {rowElement}
        </TableRowContextMenu>
      )}
    />
  );
  if (isLoading) {
    policiesBody = (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  } else if (policyRows.length === 0) {
    policiesBody = policiesEmptyState;
  }

  const activeQuarantines = quarantinesData?.quarantines ?? [];
  let quarantinesBody =
    activeQuarantines.length > 0 ? (
      <>
        <div className="hidden sm:block">
          <Table
            columns={quarantineColumns}
            data={activeQuarantines}
            rowKey={(row) => row.id}
          />
        </div>
        <div className="divide-y border-y sm:hidden">
          {activeQuarantines.map((row) => (
            <div className="space-y-3 py-4" key={row.id}>
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 break-all font-mono text-sm">
                  {row.sessionId}
                </span>
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={releaseQuarantineMutation.isPending}
                  onClick={() => releaseQuarantine(row.id)}
                >
                  <Button.Text>Release</Button.Text>
                </Button>
              </div>
              <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Policy</dt>
                <dd>{row.riskPolicyName}</dd>
                <dt className="text-muted-foreground">User</dt>
                <dd className="break-all">
                  <IdentityLink
                    identifier={row.userId ? { userId: row.userId } : null}
                  >
                    {row.userId || "Unknown user"}
                  </IdentityLink>
                </dd>
                <dt className="text-muted-foreground">Quarantined</dt>
                <dd>
                  <PolicyDateCell date={row.createdAt} />
                </dd>
              </dl>
            </div>
          ))}
        </div>
      </>
    ) : (
      <Table
        columns={quarantineColumns}
        data={activeQuarantines}
        rowKey={(row) => row.id}
        noResultsMessage={
          <Text small muted>
            No active session quarantines
          </Text>
        }
      />
    );
  if (quarantinesLoading) {
    quarantinesBody = (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  } else if (quarantinesError) {
    quarantinesBody = (
      <div className="border-border flex flex-col items-center gap-3 border py-12 text-center">
        <Text small muted>
          We couldn&apos;t load session quarantines.
        </Text>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => void refetchQuarantines()}
        >
          <RefreshCw className="h-4 w-4" />
          <Button.Text>Retry</Button.Text>
        </Button>
      </div>
    );
  }

  const primaryAction =
    isLoading || headerAction == null ? undefined : (
      <Button onClick={headerAction.onClick}>
        <Button.LeftIcon>
          <Plus className="mr-2 h-4 w-4" />
        </Button.LeftIcon>
        <Button.Text>{headerAction.label}</Button.Text>
      </Button>
    );

  return (
    <TabbedPage
      title="Guardrails"
      description="Configure the policies, detection rules, and exclusion rules that govern risk detection in agent session interactions."
      primaryAction={primaryAction}
      activeTab={activeTab}
      tabs={policyCenterTabs({ toolApprovals: toolApprovalsEnabled })}
    >
      {/* The dock follows the active tab: the Detection Rules tab took over
          the standalone page whose URL used to select these suggestions, and
          the Shadow AI targets and MCP approvals tabs mount their own config
          from their own data. */}
      {activeTab === "detection-rules" ? (
        <InsightsConfig
          contextInfo={detectionRulesInsightsContext()}
          suggestions={INSIGHTS_SUGGESTIONS["detection-rules"]}
          title="Detection rule insights"
          subtitle="Ask about rule activity, noisy rules, and coverage gaps. Match content is redacted before it reaches the assistant."
        />
      ) : activeTab === "shadow-ai-targets" ||
        activeTab === "mcp-approvals" ||
        activeTab === "tool-approvals" ? null : (
        <InsightsConfig
          contextInfo={insightsContext}
          suggestions={INSIGHTS_SUGGESTIONS["risk-policies"]}
          title="Policy insights"
          subtitle="Ask about policy status, coverage, and detector capabilities. Match content is redacted before it reaches the assistant."
        />
      )}
      {activeTab === "policies" && policiesBody}
      {activeTab === "detection-rules" && (
        <DetectionRulesTab
          createOpen={ruleCreateOpen}
          onCreateOpenChange={setRuleCreateOpen}
        />
      )}
      {activeTab === "shadow-ai-targets" && <ShadowAITargetsTab />}
      {activeTab === "mcp-approvals" && <MCPApprovalsTab />}
      {activeTab === "tool-approvals" && toolApprovalsEnabled && (
        <ToolApprovalsTab />
      )}
      {activeTab === "exclusions" && (
        <ExclusionsTab
          policies={data?.policies ?? []}
          sheet={exclusionSheet}
          onSheetChange={setExclusionSheet}
        />
      )}
      {activeTab === "quarantines" && quarantinesBody}

      {/* Delete Policy Confirmation */}
      <Dialog
        open={!!policyToDelete}
        onOpenChange={(open) => {
          if (!open) setPolicyToDelete(null);
        }}
      >
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Delete Policy</Dialog.Title>
          </Dialog.Header>
          <Stack gap={4}>
            <Text variant="body">
              <code className="bg-muted px-1 py-0.5 font-mono font-bold">
                {policyToDelete?.policy.name}
              </code>{" "}
              policy will be permanently deleted.
            </Text>
            {policyToDelete?.policy.enabled ? (
              <Text variant="body">
                To stop scanning without losing this policy, disable it instead.
              </Text>
            ) : null}
            {policyDeleteImpactText && (
              <Text variant="body">{policyDeleteImpactText}</Text>
            )}
            {policyDeleteRuleListItems.length > 0 && (
              <div className="space-y-2">
                <ul className="list-disc space-y-1 pl-5">
                  {policyDeleteRuleListItems.map((ruleName, index) => (
                    <li key={`${ruleName}-${index}`}>
                      <Text variant="body" muted as="span">
                        {ruleName}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Stack>
          <Dialog.Footer>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setPolicyToDelete(null)}
              >
                Cancel
              </Button>
              {policyToDelete?.policy.enabled ? (
                <Button
                  variant="secondary"
                  onClick={confirmDisableInstead}
                  disabled={
                    deleteMutation.isPending || toggleEnabledMutation.isPending
                  }
                >
                  Disable instead
                </Button>
              ) : null}
              <Button
                variant="destructive-primary"
                onClick={confirmDelete}
                disabled={
                  deleteMutation.isPending || toggleEnabledMutation.isPending
                }
              >
                Delete Policy
              </Button>
            </div>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </TabbedPage>
  );
}

export function PolicyAudiencePicker({
  formAudienceType,
  setFormAudienceType,
  selectedAudiencePrincipalUrns,
  setSelectedAudiencePrincipalUrns,
}: {
  formAudienceType: PolicyAudienceType;
  setFormAudienceType: (v: PolicyAudienceType) => void;
  selectedAudiencePrincipalUrns: Set<string>;
  setSelectedAudiencePrincipalUrns: (v: Set<string>) => void;
}): JSX.Element {
  const { data: rolesData } = useRoles();
  const { data: membersData } = useMembers();
  const roles = useMemo(
    () => [...(rolesData?.roles ?? [])].sort(compareRolesByName),
    [rolesData?.roles],
  );
  const members = useMemo(
    () => [...(membersData?.members ?? [])].sort(compareMembersByName),
    [membersData?.members],
  );
  const [audienceChoice, setAudienceChoice] = useState<PolicyAudienceChoice>(
    () =>
      policyAudienceChoiceForSelection(
        formAudienceType,
        selectedAudiencePrincipalUrns,
      ),
  );
  const [userSearch, setUserSearch] = useState("");

  const togglePrincipal = (principalUrn: string, checked: boolean) => {
    const next = new Set(selectedAudiencePrincipalUrns);
    if (checked) {
      next.add(principalUrn);
    } else {
      next.delete(principalUrn);
    }
    setSelectedAudiencePrincipalUrns(next);
  };

  const selectAudienceChoice = (choice: PolicyAudienceChoice) => {
    setAudienceChoice(choice);
    setFormAudienceType(choice === "everyone" ? "everyone" : "targeted");
    setSelectedAudiencePrincipalUrns(
      filterAudiencePrincipalsForChoice(selectedAudiencePrincipalUrns, choice),
    );
    if (choice !== "users") {
      setUserSearch("");
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-sm font-medium">Audience</Label>
        <p className="text-muted-foreground text-xs">
          Choose which users this policy evaluates.
        </p>
      </div>
      <RadioGroup
        value={audienceChoice}
        onValueChange={(value) =>
          selectAudienceChoice(value as PolicyAudienceChoice)
        }
      >
        <div className="border-border divide-border divide-y border">
          <PolicyAudienceChoiceRow
            id="policy-audience-everyone"
            value="everyone"
            title="Everyone"
            description="Evaluate this policy for every user in the organization."
          />
          <PolicyAudienceChoiceRow
            id="policy-audience-users"
            value="users"
            title="Specific users"
            description="Search and select individual organization members."
          />
          <PolicyAudienceChoiceRow
            id="policy-audience-roles"
            value="roles"
            title="Specific roles"
            description="Evaluate this policy for every member of selected roles."
          />
        </div>
      </RadioGroup>

      {audienceChoice === "users" && (
        <SpecificUsersAudienceSection
          members={members}
          userSearch={userSearch}
          setUserSearch={setUserSearch}
          selectedAudiencePrincipalUrns={selectedAudiencePrincipalUrns}
          onTogglePrincipal={togglePrincipal}
        />
      )}

      {audienceChoice === "roles" && (
        <div className="border-border border">
          <AudiencePrincipalSection title="Roles">
            {roles.length === 0 ? (
              <p className="text-muted-foreground px-4 py-3 text-sm">
                No roles available.
              </p>
            ) : (
              roles.map((role) => {
                const principalUrn = role.principalUrn;
                return (
                  <AudiencePrincipalRow
                    key={principalUrn}
                    id={`audience-${principalUrn}`}
                    checked={selectedAudiencePrincipalUrns.has(principalUrn)}
                    title={role.name}
                    subtitle={`${role.memberCount} members`}
                    onCheckedChange={(checked) =>
                      togglePrincipal(principalUrn, checked)
                    }
                  />
                );
              })
            )}
          </AudiencePrincipalSection>
          {selectedAudiencePrincipalUrns.size === 0 && (
            <p className="text-muted-foreground border-border border-t px-4 py-3 text-xs">
              Select at least one role to save a targeted policy.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function SpecificUsersAudienceSection({
  members,
  userSearch,
  setUserSearch,
  selectedAudiencePrincipalUrns,
  onTogglePrincipal,
  emptyHint = "Select at least one user to save a targeted policy.",
  idPrefix = "audience",
}: {
  members: AccessMember[];
  userSearch: string;
  setUserSearch: (value: string) => void;
  selectedAudiencePrincipalUrns: Set<string>;
  onTogglePrincipal: (principalUrn: string, checked: boolean) => void;
  /** Shown while no user is selected; null shows nothing. */
  emptyHint?: string | null;
  /** Keeps checkbox ids unique when two user pickers share a page. */
  idPrefix?: string;
}): JSX.Element {
  const memberByPrincipalUrn = useMemo(
    () => new Map(members.map((member) => [member.principalUrn, member])),
    [members],
  );
  const selectedUserPrincipalUrns = useMemo(
    () =>
      [...selectedAudiencePrincipalUrns]
        .filter((principalUrn) => principalUrn.startsWith("user:"))
        .sort((a, b) => {
          const aMember = memberByPrincipalUrn.get(a);
          const bMember = memberByPrincipalUrn.get(b);
          const aLabel = aMember ? memberDisplayName(aMember) : a;
          const bLabel = bMember ? memberDisplayName(bMember) : b;
          return aLabel.localeCompare(bLabel);
        }),
    [memberByPrincipalUrn, selectedAudiencePrincipalUrns],
  );
  const selectedUserOptions = selectedUserPrincipalUrns.map((principalUrn) => {
    const member = memberByPrincipalUrn.get(principalUrn);
    return {
      member,
      principalUrn,
      title: member ? memberDisplayName(member) : principalUrn,
      subtitle: member?.email ?? "Unknown user",
    };
  });
  const matchingMembers = useMemo(
    () => members.filter((member) => memberMatchesSearch(member, userSearch)),
    [members, userSearch],
  );
  const unselectedMatchingMembers = matchingMembers.filter(
    (member) => !selectedAudiencePrincipalUrns.has(member.principalUrn),
  );
  const visibleSearchResults = unselectedMatchingMembers.slice(
    0,
    USER_SEARCH_RESULT_LIMIT,
  );
  const hiddenResultCount = Math.max(
    unselectedMatchingMembers.length - visibleSearchResults.length,
    0,
  );
  const hasSearch = userSearch.trim().length > 0;

  return (
    <div className="border-border border">
      <div className="space-y-4 p-4">
        <SearchBar
          value={userSearch}
          onChange={setUserSearch}
          placeholder="Search users by name or email"
          className="w-full"
        />

        {selectedUserOptions.length > 0 && (
          <div className="space-y-2">
            <div className="text-muted-foreground text-xs font-medium">
              Selected users
            </div>
            <div className="border-border divide-border divide-y overflow-hidden border">
              {selectedUserOptions.map((option) => (
                <AudiencePrincipalRow
                  key={option.principalUrn}
                  id={`${idPrefix}-selected-${option.principalUrn}`}
                  checked
                  title={option.title}
                  subtitle={option.subtitle}
                  leading={
                    <AudienceMemberAvatar
                      name={option.member?.name ?? option.title}
                      email={option.member?.email ?? option.subtitle}
                      photoUrl={option.member?.photoUrl}
                    />
                  }
                  onCheckedChange={(checked) =>
                    onTogglePrincipal(option.principalUrn, checked)
                  }
                />
              ))}
            </div>
          </div>
        )}

        <UserSearchResults
          hasSearch={hasSearch}
          hiddenResultCount={hiddenResultCount}
          results={visibleSearchResults}
          selectedAudiencePrincipalUrns={selectedAudiencePrincipalUrns}
          onTogglePrincipal={onTogglePrincipal}
          idPrefix={idPrefix}
        />
      </div>

      {selectedUserPrincipalUrns.length === 0 && emptyHint !== null && (
        <p className="text-muted-foreground border-border border-t px-4 py-3 text-xs">
          {emptyHint}
        </p>
      )}
    </div>
  );
}

function UserSearchResults({
  hasSearch,
  hiddenResultCount,
  results,
  selectedAudiencePrincipalUrns,
  onTogglePrincipal,
  idPrefix,
}: {
  hasSearch: boolean;
  hiddenResultCount: number;
  results: AccessMember[];
  selectedAudiencePrincipalUrns: Set<string>;
  onTogglePrincipal: (principalUrn: string, checked: boolean) => void;
  idPrefix: string;
}) {
  if (!hasSearch) {
    return (
      <p className="text-muted-foreground text-sm">
        Search users by name or email to add them.
      </p>
    );
  }

  if (results.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">No matching users to add.</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-muted-foreground text-xs font-medium">
        Search results
      </div>
      <div className="border-border divide-border divide-y overflow-hidden border">
        {results.map((member) => {
          const principalUrn = member.principalUrn;
          return (
            <AudiencePrincipalRow
              key={principalUrn}
              id={`${idPrefix}-result-${principalUrn}`}
              checked={selectedAudiencePrincipalUrns.has(principalUrn)}
              title={memberDisplayName(member)}
              subtitle={member.email}
              leading={
                <AudienceMemberAvatar
                  name={member.name}
                  email={member.email}
                  photoUrl={member.photoUrl}
                />
              }
              onCheckedChange={(checked) =>
                onTogglePrincipal(principalUrn, checked)
              }
            />
          );
        })}
      </div>
      {hiddenResultCount > 0 && (
        <p className="text-muted-foreground text-xs">
          Showing first {USER_SEARCH_RESULT_LIMIT} matches. Refine the search to
          narrow results.
        </p>
      )}
    </div>
  );
}

function AudienceMemberAvatar({
  name,
  email,
  photoUrl,
}: {
  name: string;
  email: string;
  photoUrl?: string;
}) {
  return (
    <Avatar className="h-7 w-7">
      {photoUrl && <AvatarImage src={photoUrl} alt={name || email} />}
      <AvatarFallback className="text-xs">
        {memberInitials({ name, email })}
      </AvatarFallback>
    </Avatar>
  );
}

function PolicyAudienceChoiceRow({
  id,
  value,
  title,
  description,
}: {
  id: string;
  value: PolicyAudienceChoice;
  title: string;
  description: string;
}) {
  return (
    <label
      htmlFor={id}
      className="hover:bg-muted/40 flex cursor-pointer gap-3 px-4 py-3"
    >
      <RadioGroupItem id={id} value={value} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className="text-muted-foreground block text-xs">
          {description}
        </span>
      </span>
    </label>
  );
}

export function AudiencePrincipalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="border-border border-b last:border-b-0">
      <div className="bg-muted/30 border-border border-b px-4 py-2 text-xs font-medium">
        {title}
      </div>
      <div className="divide-border divide-y">{children}</div>
    </div>
  );
}

export function AudiencePrincipalRow({
  id,
  checked,
  title,
  subtitle,
  leading,
  onCheckedChange,
}: {
  id: string;
  checked: boolean;
  title: string;
  subtitle: string;
  leading?: ReactNode;
  onCheckedChange: (checked: boolean) => void;
}): JSX.Element {
  return (
    <label
      htmlFor={id}
      className="hover:bg-muted/40 flex cursor-pointer items-start gap-3 px-4 py-3"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(!!value)}
        className="mt-0.5"
      />
      {leading}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        <span className="text-muted-foreground block truncate text-xs">
          {subtitle}
        </span>
      </span>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*  ActionBadge                                                               */
/* -------------------------------------------------------------------------- */

const ACTION_BADGE_CONFIG: Record<
  PolicyAction,
  { label: string; variant: NonNullable<BadgeProps["variant"]> }
> = {
  flag: { label: "Flag", variant: "neutral" },
  warn: { label: "Warn", variant: "warning" },
  block: { label: "Block", variant: "destructive" },
  quarantine: { label: "Quarantine", variant: "destructive" },
  require_approval: { label: "Require approval", variant: "information" },
};

function ActionBadge({ action }: { action: PolicyAction }): JSX.Element {
  const config = ACTION_BADGE_CONFIG[action] ?? ACTION_BADGE_CONFIG.flag;
  return (
    <Badge variant={config.variant}>
      <Badge.Text>{config.label}</Badge.Text>
    </Badge>
  );
}
function actionDisabledReason(
  action: PolicyAction,
  flagOnlySelected: boolean,
  mcpScoped: boolean,
): string | undefined {
  if (mcpScoped && (action === "warn" || action === "quarantine")) {
    return "MCP-scoped policies support flag and block only.";
  }
  if (flagOnlySelected && action !== "flag") {
    return "Destructive Tools and Destructive CLI Commands support flagging only.";
  }
  return undefined;
}

export function ActionPicker({
  formAction,
  setFormAction,
  flagOnlySelected = false,
  mcpScoped = false,
  pinnedAction,
}: {
  formAction: PolicyAction;
  setFormAction: (v: PolicyAction) => void;
  flagOnlySelected?: boolean;
  mcpScoped?: boolean;
  /** Pins the picker to one action and shows `reason` on every other option.
   *  For policies whose action the server refuses to change at all. */
  pinnedAction?: { value: PolicyAction; reason: string };
}): JSX.Element {
  // A pin means the server refuses any change, so it outranks the source and
  // scope rules.
  const disabledReason = (action: PolicyAction): string | undefined =>
    pinnedAction && action !== pinnedAction.value
      ? pinnedAction.reason
      : actionDisabledReason(action, flagOnlySelected, mcpScoped);

  let actionValue = formAction;
  if (pinnedAction) {
    actionValue = pinnedAction.value;
  } else if (flagOnlySelected) {
    actionValue = "flag";
  } else if (mcpScoped) {
    actionValue = mcpCompatibleAction(formAction);
  }

  return (
    <RadioGroup
      value={actionValue}
      onValueChange={(value) => {
        const action = value as PolicyAction;
        if (disabledReason(action) !== undefined) {
          return;
        }
        setFormAction(action);
      }}
      className="space-y-2.5"
    >
      {ACTION_OPTIONS.map((opt) => {
        const reason = disabledReason(opt.value);
        const disabled = reason !== undefined;
        const selected = actionValue === opt.value;

        return (
          <label
            key={opt.value}
            htmlFor={`action-${opt.value}`}
            className={cn(
              "flex items-start gap-3 border p-3.5 transition-colors",
              disabled && "border-border cursor-not-allowed opacity-60",
              !disabled &&
                selected &&
                "border-foreground bg-muted/40 cursor-pointer",
              !disabled &&
                !selected &&
                "border-border hover:bg-muted/30 cursor-pointer",
            )}
          >
            <RadioGroupItem
              value={opt.value}
              id={`action-${opt.value}`}
              className="mt-0.5"
              disabled={disabled}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <ActionBadge action={opt.value} />
                <span className="text-sm font-medium">{opt.title}</span>
              </div>
              <div className="text-muted-foreground mt-1.5 text-xs">
                {opt.description}
              </div>
              {reason && (
                <div className="text-destructive mt-1 text-xs font-medium">
                  {reason}
                </div>
              )}
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
}

/* -------------------------------------------------------------------------- */
/*  RuleSelectList                                                             */
/* -------------------------------------------------------------------------- */

/** A collapsible checkbox list of the org's custom rules. Used twice in the
 *  standard-policy wizard: in the Detect step to attach rules as detectors, and
 *  in the Scope step to attach them as exemptions. The two lists drive disjoint
 *  id sets (custom_rule_ids vs exempt_rule_ids); the caller's onToggleRule keeps
 *  them mutually exclusive. */
export function RuleSelectList({
  title,
  description,
  idPrefix,
  customRules,
  selectedRuleIds,
  onToggleRule,
  expanded,
  onToggle,
}: {
  title: string;
  description: ReactNode;
  idPrefix: string;
  customRules: ReturnType<typeof useDetectionRulesStore>["customRules"];
  selectedRuleIds: Set<string>;
  onToggleRule: (ruleId: string, checked: boolean) => void;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const selectedCount = customRules.filter((r) =>
    selectedRuleIds.has(r.id),
  ).length;
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2"
      >
        <ChevronRight
          className={cn(
            "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
            expanded && "rotate-90",
          )}
        />
        <Label className="cursor-pointer text-sm font-medium">{title}</Label>
        {selectedCount > 0 && (
          <Badge variant="neutral">
            <Badge.Text>{selectedCount} selected</Badge.Text>
          </Badge>
        )}
      </button>
      {expanded && (
        <div className="border-border divide-border divide-y border">
          <p className="text-muted-foreground px-4 py-3 text-xs">
            {description}
          </p>
          <div className="space-y-2 px-4 py-3">
            {customRules.map((rule) => (
              <div key={rule.id} className="flex items-center gap-3 py-1">
                <Checkbox
                  id={`${idPrefix}-${rule.id}`}
                  checked={selectedRuleIds.has(rule.id)}
                  onCheckedChange={(next) => onToggleRule(rule.id, !!next)}
                />
                <label
                  htmlFor={`${idPrefix}-${rule.id}`}
                  className="min-w-0 flex-1 cursor-pointer truncate text-xs"
                >
                  <span className="text-foreground">
                    {rule.title || rule.id}
                  </span>
                  <span className="text-muted-foreground ml-2 font-mono text-[10px]">
                    {rule.id}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
